import { ref } from "vue"
import { logger } from "../utils/logger"
import { authService } from "./authService"

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? ""

export interface ApiRequestOptions {
  method?: string
  body?: unknown
  // The active household id, or null/undefined for personal context. Sent as
  // the X-Household-Id header the API uses to scope the request.
  householdId?: string | null
  // Abort the request after this many ms. Turns an indefinite hang (which mobile
  // Safari surfaces as an opaque "Load failed") into a clean, timed-out error.
  timeoutMs?: number
}

async function buildHeaders(householdId: string | null | undefined): Promise<Record<string, string>> {
  const headers: Record<string, string> = { "Content-Type": "application/json" }

  const token = await authService.getIdToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  if (householdId) {
    headers["X-Household-Id"] = householdId
  }
  return headers
}

// ---------------------------------------------------------------------------
// Offline outbox
//
// Mutating requests (POST/PATCH/PUT/DELETE) that fail at the network level
// (fetch threw — not an HTTP error response) are queued in localStorage and
// replayed in order when connectivity returns. Each entry remembers the
// household id it was issued under so the replay hits the same context.
// ---------------------------------------------------------------------------

const OUTBOX_STORAGE_KEY = "apiOutbox"
const OUTBOX_MAX_ENTRIES = 50
const OUTBOX_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

interface OutboxEntry {
  method: string
  path: string
  body?: unknown
  // Household context the request was issued under (X-Household-Id header),
  // or null for personal context. Preserved so replay never runs against a
  // different household than the one active at enqueue time.
  householdId: string | null
  timestamp: number
}

const MUTATING_METHODS = new Set(["POST", "PATCH", "PUT", "DELETE"])

// Paths the offline outbox must never queue or replay. These are interactive,
// single-use, or non-idempotent action ("RPC") endpoints rather than data
// edits — replaying them later does nothing useful and actively causes harm:
// e.g. each replayed /api/bank/authorize mints a fresh (already-stale) bank-auth
// link and inserts an orphan connection row, and surfaces a bogus "N changes
// waiting to sync" to the user. Likewise a queued /api/subscriptions/cancel or
// /api/invitations accept/decline replayed hours later silently cancels a
// subscription (or joins a household) the user believed had failed. Only plain
// resource mutations belong in the outbox.
// ("/api/account" is the account-action namespace — deliberately distinct
// from the "/api/accounts" resource, which stays replayable.)
const NON_REPLAYABLE_PREFIXES = [
  "/api/bank",
  "/api/ai",
  "/api/data",
  "/api/subscriptions",
  "/api/invitations",
  "/api/account",
]

function isReplayablePath(path: string): boolean {
  // Match the bare namespace root too (POST /api/invitations, DELETE
  // /api/data) — a trailing-slash-only prefix used to let those through the
  // outbox. Segment-aware so "/api/account" never swallows "/api/accounts".
  const bare = path.split("?")[0]
  return !NON_REPLAYABLE_PREFIXES.some((prefix) => bare === prefix || bare.startsWith(`${prefix}/`))
}

// Marker for "the request never reached the server but is queued in the
// outbox and will replay". Optimistic-update callers keep their local state
// in that case instead of rolling back.
const QUEUED_FLAG = "queuedInOutbox"

export function isQueuedOfflineError(error: unknown): boolean {
  return error instanceof Error && (error as Error & { [QUEUED_FLAG]?: boolean })[QUEUED_FLAG] === true
}

function readOutbox(): OutboxEntry[] {
  try {
    const raw = localStorage.getItem(OUTBOX_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

// Number of queued offline mutations — reactive so UI (the offline banner)
// can show "N changes waiting to sync".
export const pendingOutboxCount = ref(0)

function writeOutbox(entries: OutboxEntry[]): void {
  try {
    localStorage.setItem(OUTBOX_STORAGE_KEY, JSON.stringify(entries))
  } catch (error) {
    logger.warn("Failed to persist offline outbox", { error })
  }
  pendingOutboxCount.value = entries.length
}

// Tell the user when queued offline edits are discarded (queue overflow, age
// expiry, permanent 4xx on replay) — a silent drop looks like data loss with
// no explanation. Best-effort: never let the notice break the outbox itself.
function notifyDroppedEntries(count: number): void {
  if (count <= 0) return
  void (async () => {
    try {
      const { useToast } = await import("../composables/useToast")
      const { i18n } = await import("../i18n")
      useToast().warning(i18n.global.t("offline.changes_discarded", { count }), { duration: 6000 })
    } catch {
      // Toast layer unavailable (tests, very early startup) — the log below
      // already records the drop.
    }
  })()
}

function enqueueOutboxEntry(entry: OutboxEntry): void {
  const entries = readOutbox()
  entries.push(entry)
  // Capped queue: drop the oldest entries when full.
  let overflow = 0
  while (entries.length > OUTBOX_MAX_ENTRIES) {
    entries.shift()
    overflow++
  }
  writeOutbox(entries)
  notifyDroppedEntries(overflow)
  logger.warn(`Queued offline request ${entry.method} ${entry.path} (outbox: ${entries.length})`)
}

/** Drop every queued offline mutation. Called on logout so one user's pending
 * edits never replay under the next user's token on a shared device. */
export function clearOutbox(): void {
  writeOutbox([])
}

let outboxReplayInFlight: Promise<void> | null = null

// Replay queued mutations in order. Per entry:
// - network failure → keep it (and everything after it) for the next attempt
// - 401/403 → keep it; the session token likely isn't refreshed yet
// - 429 → keep it; the rate limit refills within a minute
// - other 4xx → drop it, it will never succeed
// - success / other statuses → drop it (done, or 5xx left for the next pass)
// Entries older than 7 days are pruned up-front. Serialized so overlapping
// 'online' events don't double-send.
export function replayOutbox(): Promise<void> {
  if (outboxReplayInFlight) return outboxReplayInFlight
  outboxReplayInFlight = doReplayOutbox().finally(() => {
    outboxReplayInFlight = null
  })
  return outboxReplayInFlight
}

async function doReplayOutbox(): Promise<void> {
  // Prune expired and never-replayable entries first. Only age-expired ones
  // count as user-visible drops — never-replayable paths were queued by an
  // older build and replaying them was never promised.
  const now = Date.now()
  const initial = readOutbox()
  const expired = initial.filter((e) => now - e.timestamp > OUTBOX_MAX_AGE_MS && isReplayablePath(e.path)).length
  const fresh = initial.filter((e) => now - e.timestamp <= OUTBOX_MAX_AGE_MS && isReplayablePath(e.path))
  if (fresh.length !== initial.length) {
    writeOutbox(fresh)
    notifyDroppedEntries(expired)
  }

  while (true) {
    const queue = readOutbox()
    const entry = queue[0]
    if (!entry) return

    let response: Response
    try {
      response = await fetch(`${baseUrl}${entry.path}`, {
        method: entry.method,
        headers: await buildHeaders(entry.householdId),
        body: entry.body === undefined ? undefined : JSON.stringify(entry.body),
      })
    } catch {
      // Still offline — keep the queue intact and retry on the next 'online'.
      return
    }

    if (response.status >= 500 || response.status === 429) {
      // Server-side trouble or rate-limited: keep the entry and back off
      // until the next pass.
      return
    }
    if (response.status === 401 || response.status === 403) {
      // Auth isn't re-established yet — 'online' often fires before the token
      // refresh completes. Transient, not a permanent rejection: keep the
      // entry (and everything after it) instead of silently dropping edits.
      return
    }
    if (response.status >= 400) {
      logger.warn(`Dropping queued request ${entry.method} ${entry.path}: got ${response.status}`)
      notifyDroppedEntries(1)
    }
    // Success or 4xx: remove the entry. Re-read so concurrent enqueues that
    // happened while the request was in flight are not clobbered.
    const current = readOutbox()
    current.shift()
    writeOutbox(current)
  }
}

// Drop any entries that should never have been queued (e.g. bank/ai actions
// queued by an older build). Self-healing: clears a stuck "N changes waiting to
// sync" the moment the new build loads, even while offline.
function pruneNonReplayableEntries(): void {
  const entries = readOutbox()
  const kept = entries.filter((e) => isReplayablePath(e.path))
  if (kept.length !== entries.length) {
    writeOutbox(kept)
  }
}

let outboxOnlineListener: (() => void) | null = null

// Install the 'online' replay trigger and kick off an immediate replay if we
// are (or believe we are) online. Returns a teardown that removes the
// listener. Called once at module init; exported for tests.
export function initOutbox(): () => void {
  teardownOutbox()
  outboxOnlineListener = () => {
    replayOutbox().catch((error) => logger.error("Outbox replay failed", error))
  }
  window.addEventListener("online", outboxOnlineListener)
  if (navigator.onLine && readOutbox().length > 0) {
    replayOutbox().catch((error) => logger.error("Outbox replay failed", error))
  }
  return teardownOutbox
}

export function teardownOutbox(): void {
  if (outboxOnlineListener) {
    window.removeEventListener("online", outboxOnlineListener)
    outboxOnlineListener = null
  }
}

if (typeof window !== "undefined") {
  pruneNonReplayableEntries()
  pendingOutboxCount.value = readOutbox().length
  initOutbox()
}

// ---------------------------------------------------------------------------
// Offline read cache
//
// The last successful response of every live list subscription is persisted
// so the app can render real data immediately on a cold start — and still
// render *something* when it starts offline. The cache is hydration-only:
// the API refetch that follows overwrites it the moment it succeeds.
// ---------------------------------------------------------------------------

const READ_CACHE_PREFIX = "apiCache:v1:"

function readCacheKey(path: string, householdId: string | null | undefined): string {
  return `${READ_CACHE_PREFIX}${householdId ?? "personal"}:${path}`
}

function readCachedResponse(key: string): unknown {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : undefined
  } catch {
    return undefined
  }
}

function writeCachedResponse(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Quota exceeded (e.g. large receipt images) — drop this entry rather
    // than fail; the cache is best-effort.
    try {
      localStorage.removeItem(key)
    } catch {
      // Ignore.
    }
  }
}

/** Drop all cached API responses. Called on logout so financial data doesn't
 * outlive the session in localStorage. */
export function clearApiReadCache(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key?.startsWith(READ_CACHE_PREFIX)) localStorage.removeItem(key)
    }
  } catch {
    // localStorage unavailable — nothing to clear.
  }
}

// Rows created while offline exist only as queued POSTs; the read-cache
// overlay (overlayPendingMutations) gives them synthetic `outbox-<ts>-<i>`
// ids. A PATCH/DELETE against such an id must be resolved against the queued
// POST itself — the server has never seen the id, so sending the request (or
// queueing it for replay) could only ever 404 after reconnect.
const OUTBOX_ID_RE = /^outbox-(\d+)-(\d+)$/

function tryResolveOutboxIdMutation(
  method: string,
  path: string,
  body: unknown,
  householdId: string | null,
): { result: unknown } | null {
  if (method !== "PATCH" && method !== "DELETE") return null
  const segments = path.split("?")[0].split("/")
  const last = segments[segments.length - 1] ?? ""
  const match = OUTBOX_ID_RE.exec(last)
  if (!match) return null

  const collectionPath = segments.slice(0, -1).join("/")
  const ts = Number(match[1])
  const entries = readOutbox()
  const creates = entries.filter(
    (e) => (e.householdId ?? null) === householdId && e.method === "POST" && e.path === collectionPath,
  )
  // The synthetic id encodes the create's enqueue timestamp (unique enough:
  // the index only disambiguates same-millisecond creates, and the overlay
  // enumerates them in queue order — same as here).
  const sameTs = creates.filter((e) => e.timestamp === ts)
  const target = sameTs[Number(match[2])] ?? sameTs[0]
  if (!target) {
    // The POST already replayed (the row now has a real id — the next refetch
    // delivers it) or was dropped. Either way this synthetic id is dead.
    const error = new Error(`API ${method} ${path} failed (404): pending offline row no longer exists`) as Error & {
      status?: number
    }
    error.status = 404
    throw error
  }

  if (method === "DELETE") {
    writeOutbox(entries.filter((e) => e !== target))
    return { result: undefined }
  }
  target.body = { ...(target.body as Record<string, unknown>), ...(body as Record<string, unknown>) }
  writeOutbox(entries)
  return { result: { id: last, ...(target.body as Record<string, unknown>) } }
}

// Thin fetch wrapper for the REST API: attaches the Supabase access token and
// household context, serializes JSON, and turns non-2xx responses into errors.
export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { method = "GET", body, householdId, timeoutMs } = options
  const outboxResolved = tryResolveOutboxIdMutation(method, path, body, householdId ?? null)
  if (outboxResolved) return outboxResolved.result as T
  let response: Response
  try {
    response = await fetch(`${baseUrl}${path}`, {
      method,
      headers: await buildHeaders(householdId),
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined,
    })
  } catch (error) {
    // fetch threw → network-level failure (offline, DNS, ...). Queue mutating
    // requests for replay once connectivity returns; reads just fail. Action
    // endpoints (bank/ai/...) are never queued — replaying them is useless or
    // harmful, and they shouldn't show up as "pending changes".
    if (MUTATING_METHODS.has(method) && isReplayablePath(path)) {
      enqueueOutboxEntry({ method, path, body, householdId: householdId ?? null, timestamp: Date.now() })
      if (error instanceof Error) {
        ;(error as Error & { [QUEUED_FLAG]?: boolean })[QUEUED_FLAG] = true
      }
    }
    throw error
  }

  if (!response.ok) {
    throw await buildApiError(response, method, path)
  }

  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}

// The API returns { error: { code, message } } (older routes: { error: string },
// AI routes: { error: string, code: string }); surface the message instead of
// the raw JSON body. The machine code must stay reachable — callers all over
// the app branch on message.includes("quota_exceeded") etc., so it is kept in
// the message text (and on error.code).
async function buildApiError(
  response: Response,
  method: string,
  path: string,
): Promise<Error & { status?: number; code?: string }> {
  const detail = await response.text().catch(() => "")
  let message = detail
  let code: string | undefined
  try {
    const parsed = JSON.parse(detail) as {
      error?: string | { code?: string; message?: string }
      code?: string
    }
    if (typeof parsed.error === "string") {
      message = parsed.error
      if (typeof parsed.code === "string") code = parsed.code
    } else if (parsed.error && typeof parsed.error.message === "string") {
      message = parsed.error.message
      if (typeof parsed.error.code === "string") code = parsed.error.code
    }
  } catch {
    // Body wasn't JSON — keep the raw text.
  }
  const error = new Error(
    `API ${method} ${path} failed (${response.status})${message ? `: ${message}` : ""}${code ? ` [${code}]` : ""}`,
  ) as Error & { status?: number; code?: string }
  error.status = response.status
  error.code = code
  return error
}

// Multipart upload (images). Deliberately NOT routed through the offline
// outbox: entries are persisted to localStorage as JSON, and a File can't be
// serialized that way — a queued upload would replay as an empty body. Failing
// closed while offline is honest; the caller shows "you're offline" and the
// user re-picks the photo when they're back.
export async function apiUpload<T>(
  path: string,
  form: FormData,
  options: { householdId?: string | null; timeoutMs?: number } = {},
): Promise<T> {
  const headers = await buildHeaders(options.householdId)
  // Let the browser set Content-Type: it has to include the multipart boundary.
  delete headers["Content-Type"]

  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers,
    body: form,
    signal: options.timeoutMs ? AbortSignal.timeout(options.timeoutMs) : undefined,
  })
  if (!response.ok) {
    throw await buildApiError(response, "POST", path)
  }
  return (await response.json()) as T
}

// Absolute URL for an API path — images are rendered by <img src>, which can't
// go through the fetch wrapper.
export function apiUrl(path: string): string {
  return `${baseUrl}${path}`
}

export type Unsubscribe = () => void

// Registry of every live subscription's refetch closure. Realtime keeps these
// fresh automatically, but a manual trigger (pull-to-refresh, "stale tab"
// resume) can call `refetchAllSubscriptions()` to force every active resource
// to re-pull from the API at once.
const activeRefetchers = new Set<() => Promise<void>>()

// Force all live `subscribeViaApi` subscriptions to refetch and resolve once
// they've all settled. Used by pull-to-refresh so the spinner stays up until
// fresh data has actually landed. Failures are swallowed per-resource (already
// logged) so one bad endpoint doesn't reject the whole refresh; `anyFailed`
// lets the caller tell the user the refresh didn't fully land.
export function refetchAllSubscriptions(): Promise<{ anyFailed: boolean }> {
  return Promise.all(
    [...activeRefetchers].map((refetch) =>
      refetch().then(
        () => true,
        () => false,
      ),
    ),
  ).then((results) => ({ anyFailed: results.some((ok) => !ok) }))
}

// List refetches are plain idempotent GETs, so a timeout is safe — it turns an
// indefinite mobile-radio hang (pull-to-refresh spinner that never resolves)
// into a clean failure. Mutations deliberately get NO default timeout: an
// aborted mutation is queued in the offline outbox and replayed, which would
// double-apply if the original request did reach the server.
const LIST_REFETCH_TIMEOUT_MS = 15000

// Initial fetch + registration in the refetch pool. Live updates come from
// refetchPolling (60s visible-tab poll + refetch-on-foreground) since phase 3
// retired Supabase Realtime along with the rest of Supabase; `table` and
// `options.filter` are kept so the many call sites didn't need to change.
// Overlay queued offline mutations onto a cached list, so a row created or
// edited offline survives an app restart that happens before connectivity
// returns. Without this the cold-start hydration below replays the
// pre-mutation snapshot and the user's offline entry looks lost until the
// outbox replays. Request bodies and cached rows share the same camelCase
// shape, so the overlay is a straight merge; the next successful refetch
// replaces it all with server truth.
function overlayPendingMutations(path: string, householdId: string | null, cached: unknown): unknown {
  const pending = readOutbox().filter((e) => (e.householdId ?? null) === householdId && isReplayablePath(e.path))
  const creates = pending.filter((e) => e.method === "POST" && e.path === path)
  const edits = pending.filter((e) => (e.method === "PATCH" || e.method === "DELETE") && e.path.startsWith(`${path}/`))
  if (creates.length === 0 && edits.length === 0) return cached
  // Only list responses can be overlaid; leave single-object caches alone.
  if (cached !== undefined && !Array.isArray(cached)) return cached
  if (cached === undefined && creates.length === 0) return undefined

  let rows: Record<string, unknown>[] = Array.isArray(cached) ? [...(cached as Record<string, unknown>[])] : []
  for (const e of edits) {
    const id = e.path.slice(path.length + 1).split("?")[0]
    if (e.method === "DELETE") {
      rows = rows.filter((row) => row.id !== id)
    } else {
      rows = rows.map((row) => (row.id === id ? { ...row, ...(e.body as Record<string, unknown>) } : row))
    }
  }
  creates.forEach((e, i) => {
    const ts = new Date(e.timestamp).toISOString()
    rows.unshift({
      ...(e.body as Record<string, unknown>),
      id: `outbox-${e.timestamp}-${i}`,
      createdAt: ts,
      updatedAt: ts,
    })
  })
  return rows
}

export function subscribeViaApi<T>(
  table: string,
  path: string,
  callback: (rows: T) => void,
  options: { householdId?: string | null; filter?: string } = {},
): Unsubscribe {
  // Hydrate from the offline read cache first: instant paint on cold start,
  // and last-known data when starting offline. The fetch below overwrites it.
  const cacheKey = readCacheKey(path, options.householdId)
  const cached = overlayPendingMutations(path, options.householdId ?? null, readCachedResponse(cacheKey))
  if (cached !== undefined) callback(cached as T)

  // Once torn down, an in-flight fetch must not deliver: on a household
  // switch the old context's GET can otherwise resolve AFTER the new
  // context's data loaded and overwrite it with the previous household's
  // rows (last-write-wins between contexts on a slow connection).
  let disposed = false

  // Rejects on failure (after logging) so refetchAllSubscriptions can report
  // a partial refresh; the fire-and-forget call sites below swallow it.
  const refetch = (): Promise<void> =>
    apiRequest<T>(path, { householdId: options.householdId, timeoutMs: LIST_REFETCH_TIMEOUT_MS })
      .then((rows) => {
        if (disposed) return
        writeCachedResponse(cacheKey, rows)
        callback(rows)
      })
      .catch((error) => {
        logger.error(`Failed to refetch ${table} from API`, error)
        throw error
      })
  refetch().catch(() => {})
  activeRefetchers.add(refetch)

  return () => {
    disposed = true
    activeRefetchers.delete(refetch)
  }
}
